import React, { useEffect, useRef } from "react";
import { RiDeleteBin6Line, RiAddLine } from "react-icons/ri";
import styles from "./ApiConfigGB.module.css"; // reuse from CustomAPIConfig if you like
import { FaAngleDown } from "react-icons/fa";

const STATUS_OPTIONS = [
  "200",
  "201",
  "202",
  "204",
  "400",
  "401",
  "403",
  "404",
  "409",
  "422",
  "429",
  "500",
  "502",
  "503",
  "504",
];

// ─── Concrete-sample parser ─────────────────────────────────────────
// Parse the sample payload string into an object suitable for
// original_curl_config.payload. Values that look like placeholders
// ({{ ... }}) are dropped, because they would poison downstream type
// inference in IntentIndex.js.
//
// Returns null if the payload can't be parsed or isn't a JSON object.
const PLACEHOLDER_RE = /\{\{\s*[^}]+?\s*\}\}/;

const isPlaceholderValue = (v) => {
  if (typeof v === "string") return PLACEHOLDER_RE.test(v);
  if (Array.isArray(v)) return v.some(isPlaceholderValue);
  if (v && typeof v === "object") {
    return Object.values(v).some(isPlaceholderValue);
  }
  return false;
};

const stripPlaceholders = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(stripPlaceholders).filter((v) => v !== undefined);
  }
  if (obj && typeof obj === "object") {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
      if (isPlaceholderValue(v)) continue;
      out[k] = stripPlaceholders(v);
    }
    return out;
  }
  return obj;
};

const parseConcreteSampleValues = (payloadString) => {
  if (!payloadString || typeof payloadString !== "string") return null;
  const trimmed = payloadString.trim();
  if (!trimmed) return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      // Only handle object payloads for concrete-value extraction. An
      // array or scalar top-level payload has no named keys to look up.
      return null;
    }
    return stripPlaceholders(parsed);
  } catch {
    return null;
  }
};

const SampleDataEditor = ({
  sampleConfig,
  onSampleChange,
  onSampleHeaderChange,
  onSampleAddHeader,
  onSampleRemoveHeader,
  onSampleQueryChange,
  onSampleAddQuery,
  onSampleRemoveQuery,
  sampleResponses,
  onSampleResponseChange,
  onAddSampleResponse,
  onRemoveSampleResponse,
  onInfer, // callback: () => void (triggers inference)
  isConfigured = false,
  samplePayloadHint,
  // Auth-config-only UI props. Defaults preserve regular-config behavior.
  requestKeysValid = true,
  missingRequestKeys = [],
  requiredResponseKeys = [],
  responseKeysValid = true,
  // NEW: optional callback. When provided, we call it with the parsed
  // concrete values from the sample payload whenever the payload
  // changes (debounced). The parent can then write them into
  // original_curl_config.payload, so IntentIndex.js's type and format
  // inference always has good data to work with.
  onSamplePayloadSync = null,
}) => {
  // Check if method is GET to hide payload
  const isGetMethod = sampleConfig?.sampleMethod?.toUpperCase() === "GET";

  // Whether to visually flag the request payload textarea.
  const showRequestError =
    !requestKeysValid &&
    Array.isArray(missingRequestKeys) &&
    missingRequestKeys.length > 0;

  // Whether to visually flag the first 2xx response textarea.
  const showResponseError =
    !responseKeysValid &&
    Array.isArray(requiredResponseKeys) &&
    requiredResponseKeys.length > 0;

  // Whether the Auto Generate button is blocked by missing request keys.
  const isInferBlocked = !requestKeysValid;

  // ─── Debounced sync of concrete sample values ─────────────────────
  // Whenever the sample payload text changes, parse it and hand the
  // cleaned object to the parent. Debounced so we don't spam state
  // updates while the user is still typing.
  const syncTimerRef = useRef(null);
  const lastSyncedRef = useRef(null);

  useEffect(() => {
    if (!onSamplePayloadSync) return;
    if (isGetMethod) return; // no payload for GET
    const payloadStr = sampleConfig?.samplePayload || "";
    if (!payloadStr.trim()) return;

    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      const parsed = parseConcreteSampleValues(payloadStr);
      // Compare to the last value we sent to avoid redundant callbacks.
      const serialized = parsed ? JSON.stringify(parsed) : null;
      if (serialized === lastSyncedRef.current) return;
      lastSyncedRef.current = serialized;
      try {
        onSamplePayloadSync(parsed);
      } catch (e) {
        // Never let a parent callback error break the editor.
        // eslint-disable-next-line no-console
        console.warn("[SampleDataEditor] onSamplePayloadSync failed:", e);
      }
    }, 500);

    return () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    };
  }, [sampleConfig?.samplePayload, isGetMethod, onSamplePayloadSync]);

  return (
    <div className={styles.container}>
      {/* ---- URL ---- */}
      <div className={styles.formGroup}>
        <label className={styles.label}>
          <strong>API URL (full hardcoded URL):</strong>
          <input
            type="text"
            value={sampleConfig.sampleUrl || ""}
            onChange={(e) => onSampleChange("sampleUrl", e.target.value)}
            placeholder="https://api.example.com/bookings/2"
            className={styles.input}
            disabled={isConfigured}
          />
        </label>
      </div>

      {/* ---- Method ---- */}
      <div className={styles.formGroup}>
        <label className={styles.label}>
          <strong>HTTP Method:</strong>
          <div className={styles.selectWrapper}>
            <select
              value={sampleConfig.sampleMethod}
              onChange={(e) => onSampleChange("sampleMethod", e.target.value)}
              className={`${styles.select} ${styles.customSelect}`}
              disabled={isConfigured}
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
            </select>
            <FaAngleDown className={styles.selectIcon} />
          </div>
        </label>
      </div>

      {/* ---- Headers ---- */}
      <div className={styles.formGroup}>
        <label className={styles.label}>
          <div className={styles.sampleWrap}>
            <strong>Sample Headers (raw values):</strong>
            <button
              onClick={onSampleAddHeader}
              className={styles.addButton}
              disabled={isConfigured}
            >
              <RiAddLine size={18} /> Add Header
            </button>
          </div>
          <div className={styles.itemsContainer}>
            {Object.entries(sampleConfig.sampleHeaders || {}).map(
              ([key, value], idx) => (
                <div key={idx} className={styles.itemRow}>
                  <input
                    type="text"
                    placeholder="Header Name"
                    value={key}
                    onChange={(e) => {
                      onSampleRemoveHeader(key);
                      onSampleHeaderChange(e.target.value, value);
                    }}
                    className={styles.itemInput}
                    disabled={isConfigured}
                  />
                  <input
                    type="text"
                    placeholder="Header Value"
                    value={value}
                    onChange={(e) => onSampleHeaderChange(key, e.target.value)}
                    className={styles.itemInput}
                    disabled={isConfigured}
                  />
                  <button
                    onClick={() => onSampleRemoveHeader(key)}
                    className={styles.removeButton}
                    disabled={isConfigured}
                  >
                    <RiDeleteBin6Line size={15} />
                  </button>
                </div>
              ),
            )}
          </div>
        </label>
      </div>

      {/* ---- Query Params ---- */}
      <div className={styles.formGroup}>
        <label className={styles.label}>
          <div className={styles.sampleWrap}>
            <strong>Sample Query Parameters:</strong>
            <button
              onClick={onSampleAddQuery}
              className={styles.addButton}
              disabled={isConfigured}
            >
              <RiAddLine size={18} /> Add Query Param
            </button>
          </div>
          <div className={styles.itemsContainer}>
            {Object.entries(sampleConfig.sampleQueryParams || {}).map(
              ([key, value], idx) => (
                <div key={idx} className={styles.itemRow}>
                  <input
                    type="text"
                    placeholder="Param Name"
                    value={key}
                    onChange={(e) => {
                      onSampleRemoveQuery(key);
                      onSampleQueryChange(e.target.value, value);
                    }}
                    className={styles.itemInput}
                    disabled={isConfigured}
                  />
                  <input
                    type="text"
                    placeholder="Param Value"
                    value={value}
                    onChange={(e) => onSampleQueryChange(key, e.target.value)}
                    className={styles.itemInput}
                    disabled={isConfigured}
                  />
                  <button
                    onClick={() => onSampleRemoveQuery(key)}
                    className={styles.removeButton}
                    disabled={isConfigured}
                  >
                    <RiDeleteBin6Line size={15} />
                  </button>
                </div>
              ),
            )}
          </div>
        </label>
      </div>

      {/* ---- Payload - HIDDEN FOR GET ---- */}
      {!isGetMethod && (
        <div className={styles.formGroup}>
          <label className={styles.label}>
            <strong>Sample Payload (raw JSON):</strong>
            <textarea
              value={sampleConfig.samplePayload || "{}"}
              onChange={(e) => onSampleChange("samplePayload", e.target.value)}
              rows={6}
              className={styles.textarea}
              placeholder='{"room_id":2,"check_in":"2026-09-10"}'
              disabled={isConfigured}
              style={
                showRequestError
                  ? { border: "2px solid #e53e3e", outline: "none" }
                  : undefined
              }
            />
            {samplePayloadHint && (
              <div
                style={{
                  marginTop: "8px",
                  padding: "10px 12px",
                  background: "#f0f7ff",
                  borderLeft: "3px solid #4a6cf7",
                  borderRadius: "4px",
                  fontSize: "12px",
                  color: "#2d3748",
                  lineHeight: 1.6,
                  whiteSpace: "pre-line",
                  fontFamily: "monospace",
                }}
              >
                {samplePayloadHint}
              </div>
            )}
            {showRequestError && (
              <div
                style={{
                  marginTop: "6px",
                  fontSize: "12px",
                  color: "#e53e3e",
                }}
              >
                Missing required field(s): {missingRequestKeys.join(", ")}
              </div>
            )}
          </label>
        </div>
      )}

      {/* ---- Sample Responses ---- */}
      <div className={styles.formGroup}>
        <label className={styles.label}>
          <div className={styles.sampleWrap}>
            <strong>Sample Responses (JSON for each status):</strong>
            <button
              onClick={onAddSampleResponse}
              className={styles.addButton}
              disabled={isConfigured}
            >
              <RiAddLine size={18} /> Add Status
            </button>
          </div>
          {sampleResponses.map((resp, idx) => {
            const is2xx = resp.status && resp.status.startsWith("2");
            // Only flag the FIRST 2xx response textarea
            const first2xxIdx = sampleResponses.findIndex(
              (r) => r.status && r.status.startsWith("2"),
            );
            const flagThis2xx = is2xx && idx === first2xxIdx;
            const showRedHere = flagThis2xx && showResponseError;

            return (
              <div key={idx} className={styles.sampleRow}>
                <div className={styles.sampleStatusGroup}>
                  <select
                    value={resp.status}
                    onChange={(e) =>
                      onSampleResponseChange(idx, "status", e.target.value)
                    }
                    className={styles.select}
                    disabled={isConfigured}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={resp.status}
                    onChange={(e) =>
                      onSampleResponseChange(idx, "status", e.target.value)
                    }
                    placeholder="Custom status"
                    className={styles.statusInput}
                    disabled={isConfigured}
                  />
                  {sampleResponses.length > 1 && (
                    <button
                      onClick={() => onRemoveSampleResponse(idx)}
                      className={styles.removeButton}
                      disabled={isConfigured}
                    >
                      <RiDeleteBin6Line size={15} />
                    </button>
                  )}
                </div>
                <textarea
                  value={resp.body}
                  onChange={(e) =>
                    onSampleResponseChange(idx, "body", e.target.value)
                  }
                  rows={4}
                  className={styles.textarea}
                  placeholder='{"id":13,"room_id":1,...}'
                  disabled={isConfigured}
                  style={
                    showRedHere
                      ? { border: "2px solid #e53e3e", outline: "none" }
                      : undefined
                  }
                />
                {showRedHere && (
                  <div
                    style={{
                      marginTop: "4px",
                      fontSize: "12px",
                      color: "#e53e3e",
                    }}
                  >
                    Response must include: {requiredResponseKeys.join(", ")}
                  </div>
                )}
              </div>
            );
          })}
        </label>
      </div>

      {/* ---- Infer Button ---- */}
      <div className={styles.inferButtonContainer}>
        <button
          onClick={onInfer}
          className={`${styles.inferButton} ${!isConfigured && !isInferBlocked ? styles.isReady : ""}`}
          disabled={isConfigured || isInferBlocked}
          title={
            isInferBlocked
              ? `Fill in the Sample Payload first. Missing: ${missingRequestKeys.join(", ")}`
              : undefined
          }
        >
          🔄 Auto Generate API Template
        </button>
      </div>
    </div>
  );
};

export default SampleDataEditor;